import React from 'react';

import { Alert, Box, Button, FormControlLabel, Paper, Stack, Switch, Typography } from '@mui/material';

import { getMaxExtraNights, getOfferPackageName, getPackageNumberOfNights, getPackagePersistId } from './utils';

type FlexibleNightsPackage = { packageId?: string; name: string };

type Props = {
  disabled?: boolean;
  flexibleNightsById: Record<string, boolean>;
  flexibleNightsNextStepsCount: number;
  flexibleNightsWithoutMaxNights: Array<FlexibleNightsPackage>;
  formId: string;
  onConfirmDisableFlexibleNights: () => void;
  onSaved: () => void | Promise<void>;
  onToggleFlexibleNights: (packageId: string, checked: boolean) => void;
  sortedPackages: Array<App.Package>;
};

export default function PackageSetupStep({
  disabled = false,
  flexibleNightsById,
  flexibleNightsNextStepsCount,
  flexibleNightsWithoutMaxNights,
  formId,
  onConfirmDisableFlexibleNights,
  onSaved,
  onToggleFlexibleNights,
  sortedPackages,
}: Props) {
  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    await onSaved();
  }

  return (
    <Stack spacing={2}>
      {flexibleNightsWithoutMaxNights.length > 0 && (
        <Alert
          severity="warning"
          action={
            <Button color="inherit" disabled={disabled} size="small" onClick={onConfirmDisableFlexibleNights}>
              Confirm
            </Button>
          }
        >
          <Typography variant="body2">
            We were unable to link{' '}
            {flexibleNightsWithoutMaxNights.length === 1 ? 'this Deal Option' : 'these Deal Options'}, since{' '}
            {flexibleNightsWithoutMaxNights.length === 1 ? 'it is' : 'they are'} set to Flexi Night but there is no Max
            Nights set. In order to continue, please click &quot;Confirm&quot;, which will set Flexi Nights to FALSE in
            the package on Admin Portal, but will not change Salesforce:
          </Typography>
          <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
            {flexibleNightsWithoutMaxNights.map((pkg) => (
              <li key={pkg.packageId}>{pkg.name}</li>
            ))}
          </Box>
        </Alert>
      )}

      {flexibleNightsNextStepsCount > 0 && (
        <>
          <Typography variant="body2" color="text.secondary">
            Once you have finished setting up the offer, complete these steps:
          </Typography>
          <Box component="ol" sx={{ pl: 2, mt: -1 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              Set max nights for {flexibleNightsNextStepsCount === 1 ? 'this deal option' : 'these deal options'} in
              Salesforce.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              {flexibleNightsNextStepsCount === 1 ? 'This Deal Option' : 'These Deal Options'} will be automatically
              synced into Admin Portal once this is done.
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Set Flexi Nights to TRUE for {flexibleNightsNextStepsCount === 1 ? 'this package' : 'these packages'} in
              Admin Portal. This will be flagged in the CM checklist.
            </Typography>
          </Box>
        </>
      )}

      <form id={formId} onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {sortedPackages.map((pkg) => {
            const packageId = getPackagePersistId(pkg);
            const isFlexibleNights = (packageId && flexibleNightsById[packageId]) ?? false;
            const maxExtraNights = getMaxExtraNights(pkg);
            const numberOfNights = getPackageNumberOfNights(pkg);
            // Enabling requires a real synced max-nights value, so AP can't drift from Salesforce.
            const canEnableFlexibleNights = maxExtraNights > 0;

            return (
              <Paper key={packageId} variant="outlined" sx={{ borderRadius: 1.25, p: 2 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Typography sx={{ flex: 1 }}>{getOfferPackageName(pkg)}</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 70 }}>
                    {numberOfNights !== undefined ? `${numberOfNights} night${numberOfNights === 1 ? '' : 's'}` : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ minWidth: 150 }}>
                    Max extra nights (Salesforce): {maxExtraNights > 0 ? maxExtraNights : 'none set'}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isFlexibleNights}
                        disabled={disabled || (!isFlexibleNights && !canEnableFlexibleNights)}
                        onChange={(event) => packageId && onToggleFlexibleNights(packageId, event.target.checked)}
                      />
                    }
                    label="Flexible nights"
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </form>
    </Stack>
  );
}
